import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import MaskedLines from "./MaskedLines";
import { HERO_BG } from "@/constants/images";

const EASE = [0.16, 1, 0.3, 1];

export const scrollToId = (id) => {
    const el = document.querySelector(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
};

const fadeUp = (delay) => ({
    initial: { opacity: 0, y: 26 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, ease: EASE, delay },
});

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative h-[92svh] min-h-[600px] overflow-hidden md:h-[76svh] md:max-h-[780px]"
        >
            <motion.div style={{ y }} className="absolute inset-0">
                <motion.img
                    src={HERO_BG}
                    alt="Carotare în beton cu utilaje profesionale diamantate"
                    initial={{ scale: 1.18 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2.8, ease: EASE }}
                    className="h-full w-full object-cover will-change-transform"
                />
            </motion.div>

            <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/40 to-ink/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />

            <div
                aria-hidden="true"
                className="absolute left-0 top-0 hidden h-full w-px bg-gradient-to-b from-transparent via-line to-transparent lg:block"
            />

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 mx-auto flex h-full max-w-[1600px] items-start px-5 pt-20 lg:px-10 lg:pt-24"
            >
                <div className="w-full">
                    <motion.div
                        {...fadeUp(0.25)}
                        className="mb-6 flex items-center gap-4"
                    >
                        <span className="h-[2px] w-10 bg-flame" />
                        <span className="font-mono-tech text-[9px] tracking-[0.25em] text-mist sm:text-[10px]">
                            SOLUȚII DIAMANTATE PENTRU BETON ARMAT
                        </span>
                    </motion.div>

                    <MaskedLines
                        delay={0.35}
                        stagger={0.14}
                        className="font-display font-extrabold uppercase leading-[0.98] tracking-[-0.01em] text-paper"
                        lineClassName="text-[clamp(1.9rem,4.6vw,4.6rem)]"
                        lines={[
                            <>PRECIZIE ÎN BETON.</>,
                            <>
                                PUTERE ÎN FIECARE{" "}
                                <span className="text-flame">TĂIETURĂ.</span>
                            </>,
                        ]}
                    />

                    <motion.p
                        {...fadeUp(0.9)}
                        className="mt-6 max-w-xl text-sm leading-relaxed text-mist lg:text-[15px]"
                    >
                        Soluții profesionale pentru carotare, tăieri, demolări,
                        șlefuire și repastilare de carote.
                    </motion.p>

                    <motion.div
                        {...fadeUp(1.05)}
                        className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
                    >
                        <a
                            href="#servicii"
                            data-testid="hero-cta-services"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToId("#servicii");
                            }}
                            className="group inline-flex w-fit items-center gap-3 bg-flame px-7 py-4 font-mono-tech text-xs font-bold tracking-[0.2em] text-white transition-all duration-300 hover:bg-flame-hover hover:shadow-[0_0_30px_rgba(255,85,0,0.35)]"
                        >
                            DESCOPERĂ SERVICIILE
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                        </a>
                        <Link
                            to="/contact"
                            data-testid="hero-cta-contact"
                            className="group inline-flex w-fit items-center gap-3 border border-paper/25 bg-paper/5 px-7 py-4 font-mono-tech text-xs font-bold tracking-[0.2em] text-paper backdrop-blur-sm transition-all duration-300 hover:border-flame hover:bg-flame hover:text-ink"
                        >
                            CONTACTEAZĂ-NE
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                        </Link>
                    </motion.div>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 1 }}
                className="absolute bottom-8 right-5 z-10 hidden flex-col items-center gap-3 lg:flex lg:right-10"
            >
                <span className="font-mono-tech text-[10px] tracking-[0.35em] text-mist">
                    SCROLL
                </span>
                <span className="block h-14 w-px animate-scroll-line bg-gradient-to-b from-flame to-transparent" />
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                aria-hidden="true"
                className="absolute right-6 top-1/2 z-10 hidden -translate-y-1/2 lg:block"
            >
                <span
                    className="font-mono-tech text-[10px] tracking-[0.4em] text-mist/50"
                    style={{ writingMode: "vertical-rl" }}
                >
                    BETON — DIAMANT — OȚEL
                </span>
            </motion.div>
        </section>
    );
}